import React, { useState, useEffect } from "react";
import { Layout } from "antd";
import { Outlet, useLocation } from "react-router-dom";
import { baseRouteMaker, routes } from "../../../../constants/routeConsts";
import { useSelector } from "react-redux";
import { Navbar } from "../../navbar";
import { Sidebar } from "../../sidebar";
import { Drawer } from "../../drawer";
import { GetCardLayout } from "../../getCardLayout";
import styles from "./MainLayout.module.scss";
import { Box } from "../../box";
import { Tabs } from "../../tabs";
import { userRoles } from "../../../../constants";
import useLocalStorage from "../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../constants/config/appConfig";
import { useOffersAndPackagesMenuUtils } from "../../../../utils/tabNavigationUtils/provider/useOffersAndPackagesMenuUtils";
import { useCustomerDBMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useCustomerDBMenuUtils";
import { useUserManagementMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useUserManagementMenuUtils";
import { useFundManagementMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useFundManagementMenuUtils";
import { useFaqsMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useFaqsMenuUtils";
import { useSalesPageMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useSalesPageMenuUtils";
import { useReferralAndDiscountsMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useReferralAndDiscountsMenuUtils";
import { useAdsAndAddonsMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useAdsAndAddonsMenuUtils";
import { useRequestsMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useRequestsMenuUtils";
import { useReviewsMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useReviewsMenuUtils";
import { useSubscriptionsMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useSubscriptionsMenuUtils";
import { useServiceProviderDBUtils } from "../../../../utils/tabNavigationUtils/super-admin/useServiceProviderDBUtils";
import { useNotifyMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useNotifyMenuUtils";
import { useTicketManagementMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useTicketManagementMenuUtils";
import { useWalletMenuUtils } from "../../../../utils/tabNavigationUtils/provider/useWalletMenuUtils";
import { useAppointmentsMenuUtils } from "../../../../utils/tabNavigationUtils/provider/useAppointmentsMenuUtils";
import { useChatMenuUtils } from "../../../../utils/tabNavigationUtils/provider/useChatMenuUtils";
import { useCustomerDBMenuUtils as useServiceProviderCustomerDBMenuUtils } from "../../../../utils/tabNavigationUtils/provider/useCustomerDBMenuUtils";
import { useSubscriptionsAndAddonsMenuUtils } from "../../../../utils/tabNavigationUtils/provider/useSubscriptionsAndAddonsMenuUtils";
import { useChatAccessMenuUtils } from "../../../../utils/tabNavigationUtils/super-admin/useChatAccessMenuUtils";
import { useAddonsAndSponsorshipsMenuUtils } from "../../../../utils/tabNavigationUtils/provider/useAddonsAndSponsorshipsMenuUtils";
import { useStaffAppointmentsMenuUtils } from "../../../../utils/tabNavigationUtils/staff/useStaffAppointmentsMenuUtils";
import TermsAndConditionsDialog from "../../terms-and-condition-dialog/TermsAndConditionsDialog";
import { useNavigate } from "react-router-dom";
import { usePushNotificationMenuUtils } from "../../../../utils/tabNavigationUtils/provider/usePushNotificationMenuUtils";
import { useServiceProviderReviewTabMenuUtils } from "../../../../utils/tabNavigationUtils/provider/useServiceProviderReviewTabMenuUtils";
import { useHelpAndSupportMenuUtils } from "../../../../utils/tabNavigationUtils/provider/useHelpAndSupportMenuUtils";
import { useTranslation } from "react-i18next";

const MainLayout = () => {
  /* location dependencies */
  const location = useLocation();
  /* location dependencies */
  const { t } = useTranslation();

  /* local storage dependencies */
  const { userSession } = useLocalStorage(appConfig.SESSION_USER_INFO);
  /* local storage dependencies */

  const { loginDetails } = useSelector((state) => state.auth);

  /* drawer dependencies */
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* Staff tab data dependencies */
  const staffAppointmentsMenuGroup = useStaffAppointmentsMenuUtils();
  /* Staff tab data dependencies */

  const navigate = useNavigate();

  /* Provider tab data dependencies */
  const offersAndPackagesMenuGroup = useOffersAndPackagesMenuUtils(t);
  const walletMenuGroup = useWalletMenuUtils(t);
  const appointmentsMenuGroup = useAppointmentsMenuUtils(t);
  const chatMenuGroup = useChatMenuUtils(userSession, t);
  const serviceProviderCustomerDBMenuGroup =
    useServiceProviderCustomerDBMenuUtils(t);
  const subscriptionsAndAddonsMenuGroup = useSubscriptionsAndAddonsMenuUtils(t);
  const addonsAndSponsorshipsMenuGroup = useAddonsAndSponsorshipsMenuUtils(t);
  const pushNotificationMenuGroup = usePushNotificationMenuUtils(t);
  const serviceProviderReviewMenuGroup =
    useServiceProviderReviewTabMenuUtils(t);
  const helpAndSupportMenuGroup = useHelpAndSupportMenuUtils(t);
  /* Provider tab data dependencies */

  /* Super Admin tab data dependencies */
  const customerDbMenuGroup = useCustomerDBMenuUtils(t);
  const salesPageMenuGroup = useSalesPageMenuUtils(t);
  const userManagementMenuGroup = useUserManagementMenuUtils(t);
  const fundManagementMenuGroup = useFundManagementMenuUtils(t);
  const faqsMenuGroup = useFaqsMenuUtils(t);
  const referralAndDiscountCodesMenuGroup = useReferralAndDiscountsMenuUtils(t);
  const adsAndAddonsMenuGroup = useAdsAndAddonsMenuUtils(t);
  const requestsMenuGroup = useRequestsMenuUtils(t);
  const reviewsMenuGroup = useReviewsMenuUtils(t);
  const subscriptionsMenuGroup = useSubscriptionsMenuUtils(t);
  const serviceProviderDBGroup = useServiceProviderDBUtils(t);
  const notifyMenuGroup = useNotifyMenuUtils(t);
  const ticketManagementMenuGroup = useTicketManagementMenuUtils(t);
  const chatAccessMenuGroup = useChatAccessMenuUtils(t);
  /* Super Admin tab data dependencies */
  // eslint-disable-next-line no-array-constructor
  const listRoutesArray = new Array(
    ...serviceProviderDBGroup,
    ...offersAndPackagesMenuGroup,
    ...customerDbMenuGroup,
    ...salesPageMenuGroup,
    ...userManagementMenuGroup,
    ...fundManagementMenuGroup,
    ...faqsMenuGroup,
    ...referralAndDiscountCodesMenuGroup,
    ...adsAndAddonsMenuGroup,
    ...requestsMenuGroup,
    ...reviewsMenuGroup,
    ...subscriptionsMenuGroup,
    ...notifyMenuGroup,
    ...ticketManagementMenuGroup,
    ...walletMenuGroup,
    ...appointmentsMenuGroup,
    ...chatMenuGroup,
    ...serviceProviderCustomerDBMenuGroup,
    ...subscriptionsAndAddonsMenuGroup,
    ...chatAccessMenuGroup,
    ...addonsAndSponsorshipsMenuGroup,
    ...staffAppointmentsMenuGroup,
    ...pushNotificationMenuGroup,
    ...serviceProviderReviewMenuGroup,
    ...helpAndSupportMenuGroup
  );

  /* function definition to render the tabs based on modules for provider and super admin */
  const getTabsBasedOnProvider = () => {
    const pathname = location.pathname;
    if (pathname.includes(routes.OFFERS_AND_PACKAGES)) {
      return offersAndPackagesMenuGroup;
    } else if (pathname.includes(routes.WALLET)) {
      return walletMenuGroup;
    } else if (pathname.includes(routes.APPOINTMENT)) {
      return appointmentsMenuGroup;
    } else if (pathname.includes(routes.CUSTOMER_DB)) {
      return customerDbMenuGroup;
    } else if (pathname.includes(routes.CHAT)) {
      return chatMenuGroup;
    } else if (pathname.includes(routes.SERVICE_PROVIDER_CUSTOMER_DB)) {
      return serviceProviderCustomerDBMenuGroup;
    } else if (pathname.includes(routes.SUBSCRIPTIONS_AND_ADDONS)) {
      return subscriptionsAndAddonsMenuGroup;
    } else if (pathname.includes(routes.ADDONS_AND_SPONSORSHIPS)) {
      return addonsAndSponsorshipsMenuGroup;
    } else if (pathname.includes(routes.PUSH_NOTIFICATION)) {
      return pushNotificationMenuGroup;
    } else if (pathname.includes(routes.SERVICE_PROVIDER_REVIEWS)) {
      return serviceProviderReviewMenuGroup;
    } else if (pathname.includes(routes.HELP_AND_SUPPORT)) {
      return helpAndSupportMenuGroup;
    }
  };

  const getTabsBasedOnSuperAdmin = () => {
    const pathname = location.pathname;
    if (pathname.includes(routes.CUSTOMER_DB)) {
      return customerDbMenuGroup;
    } else if (pathname.includes(routes.SALES_PAGE)) {
      return salesPageMenuGroup;
    } else if (pathname.includes(routes.USER_MANAGEMENT)) {
      return userManagementMenuGroup;
    } else if (pathname.includes(routes.FUND_MANAGEMENT)) {
      return fundManagementMenuGroup;
    } else if (pathname.includes(routes.FAQS)) {
      return faqsMenuGroup;
    } else if (pathname.includes(routes.REFERRAL_AND_DISCOUNT_CODES)) {
      return referralAndDiscountCodesMenuGroup;
    } else if (pathname.includes(routes.ADS_AND_ADDONS)) {
      return adsAndAddonsMenuGroup;
    } else if (pathname.includes(routes.REQUESTS)) {
      return requestsMenuGroup;
    } else if (pathname.includes(routes.REVIEWS)) {
      return reviewsMenuGroup;
    } else if (pathname.includes(routes.SUBSCRIPTIONS)) {
      return subscriptionsMenuGroup;
    } else if (pathname.includes(routes.SERVICE_PROVIDER)) {
      return serviceProviderDBGroup;
    } else if (pathname.includes(routes.OFFERS_AND_PACKAGES)) {
      return offersAndPackagesMenuGroup;
    } else if (pathname.includes(routes.NOTIFY)) {
      return notifyMenuGroup;
    } else if (pathname.includes(routes.TICKET_MANAGEMENT)) {
      return ticketManagementMenuGroup;
    } else if (pathname.includes(routes.CHAT_ACCESS)) {
      return chatAccessMenuGroup;
    }
  };

  const getTabsBasedOnStaff = () => {
    const pathname = location.pathname;
    if (pathname.includes(routes.APPOINTMENT)) {
      return appointmentsMenuGroup;
    } else if (pathname.includes(routes.CHAT)) {
      return chatMenuGroup;
    } else if (pathname.includes(routes.SERVICE_PROVIDER_REVIEWS)) {
      return serviceProviderReviewMenuGroup;
    }
  };

  /* function definition to render the tabs based on modules for provider and super admin */

  /* tab dependencies */
  const renderRoleBasedTabUtil = {
    [userRoles.SERVICE_PROVIDER_ADMIN]: getTabsBasedOnProvider(),
    [userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF]:
      getTabsBasedOnStaff(),
    [userRoles.SERVICE_PROVIDER_ADMIN_ROLES.MEDICAL_STAFF]:
      getTabsBasedOnStaff(),
    [userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR]: getTabsBasedOnStaff(),
    [userRoles.SUPER_ADMIN]: getTabsBasedOnSuperAdmin(),
    [userRoles.SUPER_ADMIN_ROLES.ACCOUNTANT]: getTabsBasedOnSuperAdmin(),
    [userRoles.SUPER_ADMIN_ROLES.CUSTOMER_SUPPORT]: getTabsBasedOnSuperAdmin(),
  };

  const renderTabs = userSession
    ? renderRoleBasedTabUtil[userSession?.userRole]
    : [];
  /* tab dependencies */

  /* dialog dependencies */
  const [openTermsAndConditionsDialog, setOpenTermsAndConditionsDialog] =
    useState(false);
  /* dialog dependencies */

  useEffect(() => {
    let showDialog = false;
    if (loginDetails && Object.keys(loginDetails)?.length !== 0) {
      if (
        ![
          userRoles.SUPER_ADMIN,
          userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF,
          userRoles.SERVICE_PROVIDER_ADMIN_ROLES.MEDICAL_STAFF,
          userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR,
          userRoles.CUSTOMER,
        ].includes(userSession?.userRole)
      ) {
        if (!loginDetails?.terms_flag) {
          showDialog = true;
        }
      } else if (userRoles.CUSTOMER === userSession?.userRole) {
        navigate(routes.SIGN_IN);
        localStorage.clear();
      }
      setOpenTermsAndConditionsDialog(showDialog);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginDetails]);

  return (
    <>
      <Layout className={styles.mainProviderLayoutContainer}>
        <Navbar setDrawerOpen={setDrawerOpen} />
        <Layout>
          <Sidebar />
          <Layout.Content className={styles.layoutContainer}>
            <Box>
              {listRoutesArray.some(
                (element) => element.url === location.pathname
              ) ? (
                <Tabs tabsMenuUtils={renderTabs} />
              ) : location.pathname.includes("dashboard") ||
                location.pathname.includes(baseRouteMaker(routes.CHAT)) ||
                location.pathname.includes("details") ||
                location.pathname.includes("view") ||
                location.pathname.includes("chat") ? (
                <Outlet />
              ) : (
                <GetCardLayout>
                  <Outlet />
                </GetCardLayout>
              )}
            </Box>
          </Layout.Content>
        </Layout>
      </Layout>

      <Drawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
      <TermsAndConditionsDialog
        open={openTermsAndConditionsDialog}
        onClose={() => {
          setOpenTermsAndConditionsDialog(false);
        }}
      />
    </>
  );
};

export default MainLayout;
